import { ErrorOutline, SearchOffOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";

import { CardSkeleton } from "@/components/common/CardSkeleton";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import type { FittingRoomAreaUsage, FittingRoomUsage } from "@/hooks/useFittingRoomAreaUsage";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { formatDecimal, formatInteger } from "@/utils/number";

interface FittingRoomUsagePanelProps {
  areas: FittingRoomAreaUsage[];
  loading: boolean;
  error: boolean;
  locale: Intl.Locale;
  selectedRangeLabel: string;
  sideBySide: boolean;
  areaCount: number;
  loadingKey: string;
}

const mixProgressColor = (primary: string, index: number, total: number) => {
  const hex = primary.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(hex) || total <= 1) return primary;

  const amount = index / (total - 1);
  const target = [198, 220, 255];
  const source = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((value) =>
    Number.parseInt(value, 16),
  );
  const mixed = source.map((value, channel) =>
    Math.round(value + (target[channel] - value) * amount)
      .toString(16)
      .padStart(2, "0"),
  );

  return `#${mixed.join("")}`;
};

export const FittingRoomUsagePanel: React.FC<FittingRoomUsagePanelProps> = ({
  areas,
  loading,
  error,
  locale,
  selectedRangeLabel,
  sideBySide,
  areaCount,
  loadingKey,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const allRooms = areas.flatMap((area) => area.rooms);
  const maxCountLabel = formatInteger(
    Math.max(...allRooms.map((room) => room.totalSessions), 0),
    locale,
  );
  const renderedAreaCount = areas.length;
  const hasMultipleAreas = renderedAreaCount > 1;
  const usesTwoColumnLayout = renderedAreaCount === 2 || renderedAreaCount === 4;
  const maxRoomCount = Math.max(...areas.map((area) => area.rooms.length), 0);
  const shouldAlignAreaFooter = (area: FittingRoomAreaUsage) =>
    renderedAreaCount > 2 || (usesTwoColumnLayout && area.rooms.length >= maxRoomCount * 0.6);
  const areaGridColumns = usesTwoColumnLayout
    ? "repeat(2, minmax(0, 1fr))"
    : hasMultipleAreas
      ? "repeat(3, minmax(0, 1fr))"
      : "minmax(0, 1fr)";
  const skeletonAreaCount = Math.max(1, areaCount);
  const skeletonGridColumns =
    skeletonAreaCount === 2 || skeletonAreaCount === 4
      ? "repeat(2, minmax(0, 1fr))"
      : skeletonAreaCount > 1
        ? "repeat(3, minmax(0, 1fr))"
        : "minmax(0, 1fr)";
  const loadingBaseLabels = [
    t("sessionsReport.roomUsage.loading.preparing"),
    t("sessionsReport.roomUsage.loading.grouping"),
    t("sessionsReport.roomUsage.loading.comparing"),
  ];
  const loadingLoopingLabels = [
    t("sessionsReport.roomUsage.loading.finalizing"),
    t("sessionsReport.roomUsage.loading.waiting"),
  ];
  const getRoomLabel = (room: FittingRoomUsage) =>
    room.name ||
    t("sessionsReport.roomUsage.labels.unassignedRoom", {
      roomId: formatInteger(room.id, locale),
    });
  const getAreaInsight = (area: FittingRoomAreaUsage) => {
    const rankedRooms = [...area.rooms].sort(
      (left, right) => right.totalSessions - left.totalSessions,
    );
    const mostUsedRoom = rankedRooms[0];
    const leastUsedRoom = rankedRooms.findLast((room) => room.totalSessions > 0);

    if (
      !mostUsedRoom ||
      !leastUsedRoom ||
      mostUsedRoom.id === leastUsedRoom.id ||
      leastUsedRoom.totalSessions === 0
    ) {
      return null;
    }

    return t("sessionsReport.roomUsage.insight", {
      mostUsedRoom: getRoomLabel(mostUsedRoom),
      ratio: formatDecimal(mostUsedRoom.totalSessions / leastUsedRoom.totalSessions, locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      }),
      leastUsedRoom: getRoomLabel(leastUsedRoom),
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: sideBySide ? 440 : undefined,
        maxHeight: sideBySide ? 620 : undefined,
        "& > div": { height: "100%", overflow: "hidden" },
        "& > div > div:last-child": {
          overflowY: sideBySide ? "auto" : undefined,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        },
      }}
    >
      <StoreInsightsPanel
        title={
          <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", gap: 0.5 }}>
            <Box component="span">{t("sessionsReport.roomUsage.title")}</Box>
            <Box component="span" sx={{ color: "text.secondary", fontSize: 12, fontWeight: 400 }}>
              {t("sessionsReport.roomUsage.subtitle", { range: selectedRangeLabel })}
            </Box>
          </Box>
        }
      >
        {loading ? (
          <Box sx={{ minHeight: 240 }}>
            <Box sx={{ mb: 2.5 }}>
              <LongRunningDataLoader
                variant="table"
                isLoading={true}
                baseLabels={loadingBaseLabels}
                loopingLabels={loadingLoopingLabels}
                fixedDurationMs={8_000}
                locale={i18n.resolvedLanguage || i18n.language}
                loadingKey={loadingKey}
                estimateLabel={(duration) =>
                  t("storeComparison.loading.usuallyUnder", { duration })
                }
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: skeletonGridColumns,
                },
                columnGap: 5,
                rowGap: 5,
              }}
            >
              {Array.from({ length: skeletonAreaCount }).map((_, areaIndex) => (
                <Box key={`fitting-room-area-skeleton-${areaIndex}`}>
                  {skeletonAreaCount > 1 ? (
                    <CardSkeleton width="42%" height={12} sx={{ mb: 1.5 }} />
                  ) : null}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {Array.from({ length: 4 }).map((__, roomIndex) => (
                      <Box
                        key={`fitting-room-room-skeleton-${areaIndex}-${roomIndex}`}
                        sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                      >
                        <CardSkeleton width={72} height={12} />
                        <CardSkeleton sx={{ flex: 1, height: 14, borderRadius: 999 }} />
                        <CardSkeleton width={28} height={12} />
                        <CardSkeleton width={40} height={12} />
                      </Box>
                    ))}
                  </Box>
                  <CardSkeleton width="78%" height={12} sx={{ mt: 2.5 }} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : error ? (
          <PanelState
            icon={<ErrorOutline sx={{ fontSize: 36 }} />}
            label={t("sessionsReport.roomUsage.error")}
          />
        ) : areas.length === 0 ? (
          <PanelState
            icon={<SearchOffOutlined sx={{ fontSize: 40 }} />}
            label={t("sessionsReport.roomUsage.empty.title")}
            description={t("sessionsReport.roomUsage.empty.description")}
          />
        ) : (
          <Box
            sx={{
              overflowY: sideBySide ? "auto" : undefined,
              pr: sideBySide ? 0.5 : 0,
              width: "100%",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: areaGridColumns,
                },
                columnGap: 5,
                rowGap: 5,
              }}
            >
              {areas.map((area) => {
                const alignAreaFooter = shouldAlignAreaFooter(area);

                return (
                  <Box
                    key={area.id ?? "unassigned"}
                    sx={{
                      display: alignAreaFooter ? "flex" : undefined,
                      flexDirection: alignAreaFooter ? "column" : undefined,
                      height: alignAreaFooter ? "100%" : undefined,
                      minWidth: 0,
                    }}
                  >
                    {hasMultipleAreas ? (
                      <Box
                        sx={{
                          mb: 1.5,
                          color: "text.primary",
                          fontSize: 15,
                          fontWeight: 700,
                        }}
                      >
                        {area.name || t("sessionsReport.roomUsage.labels.unassignedArea")}
                      </Box>
                    ) : null}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                      {area.rooms.map((room) => {
                        const areaMaxSessions = Math.max(
                          ...area.rooms.map((areaRoom) => areaRoom.totalSessions),
                          0,
                        );
                        const areaMinSessions = Math.min(
                          ...area.rooms.map((areaRoom) => areaRoom.totalSessions),
                        );
                        const hasAreaUsageRange =
                          area.rooms.length > 1 && areaMaxSessions > areaMinSessions;
                        const usage =
                          areaMaxSessions > 0 ? (room.totalSessions / areaMaxSessions) * 100 : 0;
                        const roomIndex = area.rooms.indexOf(room);
                        const badge =
                          hasAreaUsageRange && room.totalSessions === areaMaxSessions
                            ? t("sessionsReport.roomUsage.labels.mostUsed")
                            : hasAreaUsageRange && room.totalSessions === areaMinSessions
                              ? t("sessionsReport.roomUsage.labels.leastUsed")
                              : null;

                        return (
                          <Box
                            key={room.id}
                            sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                color: "text.primary",
                                fontSize: 13,
                                flex: "0 0 auto",
                                fontWeight: 700,
                                minWidth: 0,
                                maxWidth: "40%",
                              }}
                            >
                              {allRooms.map((areaRoom) => (
                                <Box
                                  key={areaRoom.id}
                                  aria-hidden
                                  sx={{
                                    gridArea: "1 / 1",
                                    visibility: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getRoomLabel(areaRoom)}
                                </Box>
                              ))}
                              <Box
                                sx={{
                                  gridArea: "1 / 1",
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getRoomLabel(room)}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                height: 14,
                                flex: 1,
                                minWidth: 48,
                                overflow: "hidden",
                                borderRadius: 999,
                                bgcolor: "action.hover",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${usage}%`,
                                  height: "100%",
                                  borderRadius: "inherit",
                                  bgcolor: mixProgressColor(
                                    theme.palette.primary.main,
                                    roomIndex,
                                    area.rooms.length,
                                  ),
                                }}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "grid",
                                flex: "0 0 auto",
                                color: "text.primary",
                                fontSize: 13,
                                fontWeight: 700,
                              }}
                            >
                              <Box aria-hidden sx={{ gridArea: "1 / 1", visibility: "hidden" }}>
                                {maxCountLabel}
                              </Box>
                              <Box sx={{ gridArea: "1 / 1" }}>
                                {formatInteger(room.totalSessions, locale)}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "grid",
                                flex: "0 0 auto",
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Box aria-hidden sx={{ gridArea: "1 / 1", visibility: "hidden" }}>
                                {t("sessionsReport.roomUsage.labels.mostUsed")}
                              </Box>
                              <Box aria-hidden sx={{ gridArea: "1 / 1", visibility: "hidden" }}>
                                {t("sessionsReport.roomUsage.labels.leastUsed")}
                              </Box>
                              {badge ? (
                                <Box
                                  sx={{
                                    gridArea: "1 / 1",
                                    color:
                                      room.totalSessions === areaMaxSessions
                                        ? "primary.main"
                                        : "warning.main",
                                  }}
                                >
                                  {badge}
                                </Box>
                              ) : null}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    {getAreaInsight(area) ? (
                      <Box
                        sx={{
                          mt: alignAreaFooter ? "auto" : 0,
                          pt: 2.5,
                        }}
                      >
                        <Box
                          sx={{
                            pt: 2.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            color: "text.secondary",
                            fontSize: 12,
                            lineHeight: 1.5,
                          }}
                        >
                          {getAreaInsight(area)}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </StoreInsightsPanel>
    </Box>
  );
};

const PanelState: React.FC<{ icon: React.ReactNode; label: string; description?: string }> = ({
  icon,
  label,
  description,
}) => (
  <Box
    sx={{
      minHeight: 240,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      textAlign: "center",
      color: "text.secondary",
    }}
  >
    <Box sx={{ color: "text.secondary" }}>{icon}</Box>
    <Box sx={{ color: "text.primary", fontWeight: 700 }}>{label}</Box>
    {description ? <Box sx={{ maxWidth: 320, fontSize: 14 }}>{description}</Box> : null}
  </Box>
);
